import { Component, OnInit } from '@angular/core';
import { QuoteService } from '../../services/quotes.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { debounceTime, firstValueFrom, lastValueFrom, Observable, Subject } from 'rxjs';
import { ResponseQuote } from '../../types/dai-server/quotes/quote-responses';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ConfirmationService, LoadingComponent, ToastService } from '@digi-america/shared';
import { PriceType, QuoteStatus } from '../../types/dai-server/quotes/enums';
import { UtcToLocalTimePipe } from "../../../../node_modules/@digi-america/shared/src/lib/pipes/utcToLocal.pipe";
import { SessionService } from '../../services/session.service';
import { CurrencyInputComponent } from "../product-editor/currency-input.component";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpAddress, HttpQuoteLineItem } from '../../types/dai-server/quotes/http-quote';
import { FormGroupComponent } from "../product-editor/form-group.component";
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-quote-view',
  imports: [CurrencyPipe, RouterLink, LoadingComponent, UtcToLocalTimePipe, CurrencyInputComponent, FormsModule],
  templateUrl: './quote-view.component.html',
  styleUrl: './quote-view.component.scss'
})
export class QuoteViewComponent implements OnInit {
  quoteId!: string;
  quote?: ResponseQuote = undefined;
  selectedLineDetails: { quantity: number; salesNote: string; hiddenUnitMarkup: number; } = {
    quantity: 0,
    salesNote: "",
    hiddenUnitMarkup: 0
  };

  // The PDF has room for about eight lines of note, anything past that is cut off
  readonly longNoteMaxLines = 8;
  readonly longNoteMaxLength = 800;
  longNoteDraft: string = "";
  savingLongNote = false;

  readonly priceTypes = Object.values(PriceType);
  selectedPriceType: PriceType = PriceType.list;

  quantityChange$ = new Subject<{ quoteId: string, productId: string, quantity: number }>();

  constructor(private _quoteServer: QuoteService,
    private _routes: ActivatedRoute,
    private _confirm: ConfirmationService,
    private _session: SessionService,
    private _modalService: NgbModal,
    private _toast: ToastService) { }

  ngOnInit(): void {
    this._routes.params.subscribe({
      next: (values) => { this.quoteId = values['quoteId']; this.updateQuote(); }
    });

    this.quantityChange$
      .pipe(debounceTime(400))
      .subscribe(({ quoteId, productId, quantity }) => {
        this.updateLineItemQuantity(quoteId, productId, quantity);
      });
  }

  async updateQuote() {
    this.setQuote(await firstValueFrom(this._quoteServer.getQuote(this.quoteId)));
  }

  private setQuote(quote: ResponseQuote) {
    this.quote = quote;
    this.longNoteDraft = quote.longNote ?? "";
  }

  isEditable(quote: ResponseQuote): boolean {
    return quote.status === 'open';
  }

  onQuantityInput(target: unknown, quoteId: string, productId: string) {
    if (target instanceof HTMLInputElement) {
      const quantity = target.valueAsNumber;
      const lineItem = this.quote?.lineItems.find(l => l.productId === productId);
      if (!isNaN(quantity) && quantity >= this.minimumQuantity(lineItem)) {
        this.quantityChange$.next({ quoteId, productId, quantity });
      }
    }
  }

  async updateLineItemQuantity(quoteId: string, productId: string, quantity: number) {
    try {
      await lastValueFrom(this._quoteServer.updateQuoteItem(quoteId, productId, { quantity }));
    } catch (e) {
      this.showError(e);
    }
    this.updateQuote();
  }

  minimumQuantity(lineItem: HttpQuoteLineItem | undefined): number {
    return Math.max(lineItem?.minimumOrderQuantity ?? 1, 1);
  }

  /** Business rule errors come back as { error: "message" }, written to be shown to the user */
  private showError(e: unknown) {
    const message = e instanceof HttpErrorResponse && typeof e.error?.error === "string"
      ? e.error.error
      : "Something went wrong, please try again.";
    this._toast.error(message);
  }

  async changePriceType(template: any, quote: ResponseQuote) {
    this.selectedPriceType = quote.priceType;
    try {
      const result: PriceType | undefined = await this._modalService.open(template).result;
      if (result && result !== quote.priceType) {
        this.setQuote(await firstValueFrom(this._quoteServer.updatePriceType(quote.id, result)));
        this._toast.success("Price type updated.");
      }
    } catch (e) {
      // Dismissing the modal rejects, only report server errors
      if (e instanceof HttpErrorResponse) {
        this.showError(e);
      }
    }
  }

  longNoteLineCount(): number {
    return this.longNoteDraft ? this.longNoteDraft.split("\n").length : 0;
  }

  longNoteTooLong(): boolean {
    return this.longNoteDraft.length > this.longNoteMaxLength || this.longNoteLineCount() > this.longNoteMaxLines;
  }

  longNoteChanged(quote: ResponseQuote): boolean {
    return this.longNoteDraft !== (quote.longNote ?? "");
  }

  async saveLongNote(quote: ResponseQuote) {
    this.savingLongNote = true;
    try {
      // "" clears the note, null would keep the current value
      this.setQuote(await firstValueFrom(this._quoteServer.updateQuote(quote.id, { longNote: this.longNoteDraft })));
      this._toast.success("Note saved.");
    } catch (e) {
      this.showError(e);
    } finally {
      this.savingLongNote = false;
    }
  }

  async onDeleteLineItem(quoteId: string, productId: string) {
    await lastValueFrom(this._quoteServer.deleteQuoteItem(quoteId, productId));
    this.updateQuote();
  }

  downloadPdf(quote: ResponseQuote) {
    this._quoteServer.downloadQuotePdf(quote.id).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DIGI Quote ${quote.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  }

  async finalizeQuote(quote: ResponseQuote) {
    let confirmed = await this._confirm.confirm({
      title: "Finalize Quote",
      message: "Once the quote is finalized, you will no longer be able to modify, add or remove items. Proceed?",
      confirmText: "Finalize"
    });
    if (confirmed) {
      await firstValueFrom(this._quoteServer.updateQuote(quote.id, { status: QuoteStatus.finalized }));
      this.updateQuote();
    }
  }

  async priceOverride(template: any, quoteId: string, productId: string) {
    try {
      let result = await this._modalService.open(template).result;
      if (result) {
        await firstValueFrom(this._quoteServer.updatePriceOverride(quoteId, productId, result));
        this.updateQuote();
      }
    } catch (e) {

    }
  }

  async lineDetails(template: any, quoteId: string, lineItem: HttpQuoteLineItem) {
    this.selectedLineDetails = {
      quantity: lineItem.quantity,
      salesNote: lineItem.salesNote ?? "",
      hiddenUnitMarkup: lineItem.hiddenUnitMarkup ?? 0
    }
    try {
      let result = await this._modalService.open(template).result as { hiddenUnitMarkup: number; salesNote: string; };
      if (result) {
        await firstValueFrom(this._quoteServer.updateQuoteItem(quoteId, lineItem.productId, { quantity: lineItem.quantity, hiddenUnitMarkup: result.hiddenUnitMarkup, salesNote: result.salesNote }));
        this.updateQuote();
      }
    } catch (e) {

    }
  }

  async clearPriceOverride(quoteId: string, productId: string) {
    let proceed = await this._confirm.confirmDiscard("Are you sure you wish to clear a price override?");
    if (proceed) {
      await firstValueFrom(this._quoteServer.deletePriceOverride(quoteId, productId));
      this.updateQuote();
    }
  }

  hasPriceOverridePermissions(): boolean {
    return this._session.hasPermission("quote:price_override");
  }

  hasDetailsPermissions() {
    return this._session.hasPermission("quote:update_details");
  }

  formatAddressDetails(address: HttpAddress | null | undefined): string {
    const parts: string[] = [
      address?.addressLine1?.trim() ?? "",
      address?.addressLine2?.trim() ?? ""
    ];

    // Build city/state/zip with non-empty components
    const cityStateZipParts = [
      address?.city?.trim(),
      address?.state?.trim(),
      address?.zip?.trim()
    ].filter(part => part && part.length > 0) as string[];

    const cityStateZip = cityStateZipParts.join(", ");
    if (cityStateZip) {
      parts.push(cityStateZip);
    }

    // Remove any empty or null lines
    return parts.filter(part => part && part.length > 0).join("\n");
  }
}
